// src/turnos/CalendarioAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import {
  format,
  parse,
  startOfWeek as dfnsStartOfWeek,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import esES from "date-fns/locale/es";

import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  MenuItem,
} from "@mui/material";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";

const DragAndDropCalendar = withDragAndDrop(Calendar);

/* ========= Helpers 100% locales (sin UTC) ========= */
function fechaLocalYMD(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  return `${y}-${m}-${day}`;
}

function mkLocalDateTime(ymd, hm) {
  const [y, m, d] = (ymd || "").split("-").map((n) => parseInt(n, 10));
  const [hh, mm] = (hm || "00:00").split(":").map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
}

function startOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function hhmm(d) {
  const x = new Date(d);
  return `0${x.getHours()}`.slice(-2) + ":" + `0${x.getMinutes()}`.slice(-2);
}
function normalizeFin(inicio, fin) {
  const i = new Date(inicio);
  const f = new Date(fin);
  if (f <= i) f.setDate(f.getDate() + 1);
  return f;
}

/* ========= Localizer ========= */
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...(options || {}), locale: esES }),
  parse: (value, formatStr, options) =>
    parse(value, formatStr, new Date(), { ...(options || {}), locale: esES }),
  startOfWeek: (date, options) =>
    dfnsStartOfWeek(date, { weekStartsOn: 1, locale: esES, ...(options || {}) }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div style={{ lineHeight: 1.15 }}>
    <div style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
      {event.title}
    </div>
    {event.descripcion ? (
      <div
        style={{
          fontSize: 12,
          opacity: 0.9,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {event.descripcion}
      </div>
    ) : null}
  </div>
);

export const CalendarioAdmin = () => {
  const toast = useToast();
  const token = localStorage.getItem("token") || "";

  // Data
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState("");
  const [plantillas, setPlantillas] = useState([]);

  const [todosLosTurnos, setTodosLosTurnos] = useState([]);

  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [loadingTurnos, setLoadingTurnos] = useState(false);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null); // "YYYY-MM-DD"
  const [turnoEditar, setTurnoEditar] = useState(null);
  const [slotSeleccionado, setSlotSeleccionado] = useState(null);

  // Ventana corredera
  const [rangoCargado, setRangoCargado] = useState(() => {
    const ahora = new Date();
    const desde = subMonths(new Date(ahora.getFullYear(), ahora.getMonth(), 1), 3);
    const hasta = addMonths(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0), 3);
    return { desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) };
  });

  const calendarRef = useRef(null);
  const [vistaActual, setVistaActual] = useState("week");

  // =========================
  // Carga usuarios
  // =========================
  useEffect(() => {
    (async () => {
      setLoadingUsuarios(true);
      try {
        const res = await fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          toast.error("No se pudieron cargar los usuarios.");
          setUsuarios([]);
          return;
        }
        const list = Array.isArray(data) ? data : [];
        setUsuarios(list);
        if (!usuarioId && list.length > 0) setUsuarioId(String(list[0].id));
      } catch (e) {
        console.error(e);
        toast.error("Error de conexión al cargar usuarios.");
        setUsuarios([]);
      } finally {
        setLoadingUsuarios(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Carga plantillas
  // =========================
  useEffect(() => {
    (async () => {
      setLoadingPlantillas(true);
      try {
        const res = await fetch(`${Global.url}turnos/plantillas`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          setPlantillas([]);
          return;
        }
        const normalized = (Array.isArray(data) ? data : []).map((p) => ({
          ...p,
          segmentos: Array.isArray(p.segmentos) ? p.segmentos : JSON.parse(p.segmentos || "[]"),
        }));
        setPlantillas(normalized);
      } catch (e) {
        console.warn("Error al cargar plantillas:", e?.message);
        setPlantillas([]);
      } finally {
        setLoadingPlantillas(false);
      }
    })();
  }, [token, toast]);

  // =========================
  // Opciones usuario
  // =========================
  const usuarioOptions = useMemo(() => {
    return (usuarios || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre || ""} ${a.apellidos || ""}`.localeCompare(
          `${b.nombre || ""} ${b.apellidos || ""}`
        )
      );
  }, [usuarios]);

  const usuarioObj = useMemo(() => {
    return usuarioOptions.find((u) => String(u.id) === String(usuarioId)) || null;
  }, [usuarioOptions, usuarioId]);

  // =========================
  // Fetch turnos
  // =========================
  const fetchTurnos = async (desde, hasta, usuarioIdFetch) => {
    if (!usuarioIdFetch) return;
    setLoadingTurnos(true);

    const ymdDesde = fechaLocalYMD(startOfDayLocal(desde));
    const ymdHasta = fechaLocalYMD(endOfDayLocal(hasta));

    try {
      const res = await fetch(
        `${Global.url}turnos?usuario_id=${usuarioIdFetch}&desde=${ymdDesde}&hasta=${ymdHasta}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => []);
      if (!res.ok) {
        toast.error("No se pudieron cargar los turnos.");
        setTodosLosTurnos([]);
        return;
      }

      const list = Array.isArray(data) ? data : [];

      const mapped = list.map((turno) => {
        const start = new Date(turno.inicio);
        const end = new Date(turno.fin);

        let plantillaNombre = "";
        if (turno.plantilla_id) {
          const p = (plantillas || []).find((x) => String(x.id) === String(turno.plantilla_id));
          if (p) plantillaNombre = p.nombre || "";
        }

        const startHHMM = hhmm(start);
        const endHHMM = hhmm(end);
        const sameDay =
          start.getFullYear() === end.getFullYear() &&
          start.getMonth() === end.getMonth() &&
          start.getDate() === end.getDate();

        // Heurística de “día completo” (ausencia)
        const seemsAllDay =
          turno.tipo === "ausencia" &&
          startHHMM === "00:00" &&
          (endHHMM === "23:59" || endHHMM === "00:00" || (!sameDay && endHHMM === "00:00"));

        let endForRbc = end;
        let allDay = false;

        if (seemsAllDay) {
          allDay = true;
          const ymd = fechaLocalYMD(start);
          endForRbc = mkLocalDateTime(ymd, "00:00");
          endForRbc.setDate(endForRbc.getDate() + 1); // end exclusivo
        }

        return {
          id: turno.id,
          title:
            plantillaNombre ||
            (turno.tipo === "ausencia" ? turno.descripcion || "Ausencia" : "Turno laboral"),
          start,
          end: endForRbc,
          allDay,
          activo: turno.activo,
          tipo: turno.tipo,
          descripcion: turno.descripcion,
          plantilla_id: turno.plantilla_id,
        };
      });

      setTodosLosTurnos(mapped);
      setRangoCargado({ desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) });
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al cargar turnos.");
      setTodosLosTurnos([]);
    } finally {
      setLoadingTurnos(false);
    }
  };

  // Carga/recarga al cambiar usuario o plantillas
  useEffect(() => {
    if (!usuarioId) return;
    fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId, plantillas]);

  // =========================
  // Range change (para ampliar ventana cargada)
  // =========================
  const handleRangeChange = (range) => {
    let desde, hasta;

    if (Array.isArray(range)) {
      desde = startOfDayLocal(range[0]);
      hasta = endOfDayLocal(range[range.length - 1]);
    } else if (range instanceof Date) {
      desde = startOfDayLocal(range);
      hasta = endOfDayLocal(range);
    } else if (range?.start && range?.end) {
      desde = startOfDayLocal(range.start);
      hasta = endOfDayLocal(range.end);
    } else {
      const hoy = new Date();
      desde = startOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
      hasta = endOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
    }

    // Si el rango visible sale de la ventana cargada, ampliamos
    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = startOfDayLocal(subMonths(nuevoCentro, 3));
      const nuevoHasta = endOfDayLocal(
        addMonths(new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0), 3)
      );
      fetchTurnos(nuevoDesde, nuevoHasta, usuarioId);
    }
  };

  // =========================
  // Drag & Drop
  // =========================
  const handleEventDrop = async ({ event, start, end }) => {
    if (!event?.activo) return;

    const nuevaFecha = fechaLocalYMD(start);

    let hora_inicio = hhmm(start);
    let hora_fin = hhmm(end);

    // All day: guardamos como 00:00-23:59
    if (event.allDay) {
      hora_inicio = "00:00";
      hora_fin = "23:59";
    }

    const inicio = new Date(start);
    const fin = normalizeFin(start, end);

    // Solape contra TODOS los turnos cargados (sin el filtro de rangoVisible)
    const haySolape = (todosLosTurnos || []).some((ev) => {
      if (ev.id === event.id) return false;
      return ev.start < fin && ev.end > inicio;
    });

    if (haySolape) {
      toast.warning("No se puede mover: se solapa con otro turno.");
      return;
    }

    try {
      const res = await fetch(`${Global.url}turnos/editar/${event.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fecha: nuevaFecha,
          hora_inicio,
          hora_fin,
          tipo: event.tipo,
          descripcion: event.descripcion,
          plantilla_id: event.plantilla_id || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo mover el turno.");
        return;
      }

      toast.success("Turno movido.");
      fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
    } catch (e) {
      console.error(e);
      toast.error("Error de red al mover el turno.");
    }
  };

  const handleEventResize = async ({ event, start, end }) => {
    if (!event?.activo) return;

    const nuevaFecha = fechaLocalYMD(start);

    let hora_inicio = hhmm(start);
    let hora_fin = hhmm(end);

    if (event.allDay) {
      hora_inicio = "00:00";
      hora_fin = "23:59";
    }

    const inicio = new Date(start);
    const fin = normalizeFin(start, end);

    const haySolape = (todosLosTurnos || []).some((ev) => {
      if (ev.id === event.id) return false;
      return ev.start < fin && ev.end > inicio;
    });

    if (haySolape) {
      toast.warning("No se puede redimensionar: se solapa con otro turno.");
      return;
    }

    try {
      const res = await fetch(`${Global.url}turnos/editar/${event.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fecha: nuevaFecha,
          hora_inicio,
          hora_fin,
          tipo: event.tipo,
          descripcion: event.descripcion,
          plantilla_id: event.plantilla_id || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo actualizar el turno.");
        return;
      }

      toast.success("Turno actualizado.");
      fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
    } catch (e) {
      console.error(e);
      toast.error("Error de red al actualizar el turno.");
    }
  };

  // =========================
  // Select slot/event
  // =========================
  const handleSelectSlot = (slotInfo) => {
    setFechaSeleccionada(fechaLocalYMD(slotInfo.start));
    setTurnoEditar(null);
    setModalAbierto(true);
    setSlotSeleccionado({ start: slotInfo.start, end: slotInfo.end });
  };

  const handleSelectEvent = (event) => {
    setFechaSeleccionada(fechaLocalYMD(event.start));
    setTurnoEditar(event);
    setModalAbierto(true);
    setSlotSeleccionado(null);
  };

  // =========================
  // Save / Delete
  // =========================
  const handleSaveTurno = async (turno) => {
    // Plantilla
    if (turno.plantilla_id && turno.plantilla_id !== "personalizado") {
      const plantilla = (plantillas || []).find((p) => String(p.id) === String(turno.plantilla_id));
      if (!plantilla || !Array.isArray(plantilla.segmentos) || plantilla.segmentos.length === 0) {
        toast.error("Plantilla inválida.");
        return;
      }

      const bloques = plantilla.segmentos.map((seg) => {
        const start = mkLocalDateTime(turno.fecha, seg.hora_inicio);
        let end = mkLocalDateTime(turno.fecha, seg.hora_fin);
        if ((seg.hora_fin || "") < (seg.hora_inicio || "")) end.setDate(end.getDate() + 1);
        return { start, end };
      });

      const haySolape = (todosLosTurnos || []).some((ev) =>
        bloques.some((b) => ev.start < b.end && ev.end > b.start)
      );

      if (haySolape) {
        toast.warning("Algún segmento de la plantilla se solapa con otro turno.");
        return;
      }

      try {
        const res = await fetch(`${Global.url}turnos`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            usuario_id: usuarioId,
            fecha: turno.fecha,
            segmentos: plantilla.segmentos,
            tipo: "turno",
            descripcion: turno.descripcion,
            plantilla_id: plantilla.id,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(data?.message || "Error al crear turno(s) con plantilla.");
          return;
        }

        toast.success("Turno(s) creados con plantilla.");
        handleCloseModal();
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
      } catch (e) {
        console.error(e);
        toast.error("Error de conexión al crear turno(s).");
      }

      return;
    }

    // Personalizado
    const fechaYMD = turno.fecha;

    let hInicio = turno.hora_inicio;
    let hFin = turno.hora_fin;

    if (turno.allDay) {
      hInicio = "00:00";
      hFin = "23:59";
    }

    if (!hInicio || !hFin) {
      toast.warning("Indica hora de inicio y fin.");
      return;
    }

    const inicio = mkLocalDateTime(fechaYMD, hInicio);
    let fin = mkLocalDateTime(fechaYMD, hFin);
    if (fin < inicio) fin.setDate(fin.getDate() + 1);

    const esEdicion = Boolean(turnoEditar);
    const haySolape = (todosLosTurnos || []).some((ev) => {
      if (esEdicion && ev.id === turnoEditar.id) return false;
      return ev.start < fin && ev.end > inicio;
    });

    if (haySolape) {
      toast.warning("El turno se solapa con otro existente.");
      return;
    }

    try {
      if (turnoEditar) {
        const res = await fetch(`${Global.url}turnos/editar/${turnoEditar.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fecha: fechaYMD,
            hora_inicio: hInicio,
            hora_fin: hFin,
            tipo: turno.tipo,
            descripcion: turno.descripcion,
            plantilla_id: null,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(data?.message || "Error al editar el turno.");
          return;
        }

        toast.success("Turno editado.");
        handleCloseModal();
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
      } else {
        const res = await fetch(`${Global.url}turnos`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            usuario_id: usuarioId,
            fecha: fechaYMD,
            segmentos: [{ hora_inicio: hInicio, hora_fin: hFin }],
            tipo: turno.tipo,
            descripcion: turno.descripcion,
            plantilla_id: null,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(data?.message || "Error al crear el turno.");
          return;
        }

        toast.success("Turno creado.");
        handleCloseModal();
        fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión con el servidor.");
    }
  };

  const handleDeleteTurno = async () => {
    if (!turnoEditar?.id) return;
    if (!window.confirm("¿Seguro que deseas eliminar este turno?")) return;

    try {
      const res = await fetch(`${Global.url}turnos/${turnoEditar.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "Error al eliminar el turno.");
        return;
      }

      toast.success("Turno eliminado.");
      handleCloseModal();
      fetchTurnos(rangoCargado.desde, rangoCargado.hasta, usuarioId);
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al eliminar el turno.");
    }
  };

  const handleCloseModal = () => {
    setModalAbierto(false);
    setTurnoEditar(null);
    setFechaSeleccionada(null);
    setSlotSeleccionado(null);
  };

  // =========================
  // Estilo eventos
  // =========================
  const eventPropGetter = (event) => {
    const estilo = {
      borderRadius: 1,
      border: "1px solid rgba(0,0,0,0.18)",
      padding: "2px 6px",
      overflow: "hidden",
    };

    if (event.plantilla_id) {
      const plantilla = (plantillas || []).find((p) => String(p.id) === String(event.plantilla_id));
      if (plantilla?.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#1f2a37";
      }
    }

    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#ffffff";
      estilo.border = "1px solid rgba(0,0,0,0.1)";
    }

    if (event.activo === false) {
      estilo.backgroundColor = "#e5e7eb";
      estilo.color = "#6b7280";
      estilo.textDecoration = "line-through";
    }

    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#1f2a37";
    }

    return { style: estilo };
  };

  // =========================
  // Modal (MUI)
  // =========================
  const ModalTurno = () => {
    const [plantillaId, setPlantillaId] = useState("");
    const [tipo, setTipo] = useState("turno");
    const [descripcion, setDescripcion] = useState("");
    const [allDay, setAllDay] = useState(false);
    const [horaInicio, setHoraInicio] = useState("");
    const [horaFin, setHoraFin] = useState("");
    const [error, setError] = useState("");

    const plantillaResumen = useMemo(() => {
      const p = (plantillas || []).find((x) => String(x.id) === String(plantillaId));
      return Array.isArray(p?.segmentos) ? p.segmentos : [];
    }, [plantillaId, plantillas]);

    const plantillaOptions = useMemo(() => {
      const base = (plantillas || [])
        .slice()
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
        .map((p) => ({ id: String(p.id), nombre: p.nombre || `Plantilla ${p.id}` }));

      return [
        { id: "", nombre: "(Sin plantilla)" },
        ...base,
        { id: "personalizado", nombre: "Personalizado" },
      ];
    }, [plantillas]);

    const plantillaValue =
      plantillaOptions.find((o) => String(o.id) === String(plantillaId)) || plantillaOptions[0];

    useEffect(() => {
      if (!modalAbierto) return;

      setError("");
      setDescripcion(turnoEditar?.descripcion || "");
      setTipo(turnoEditar?.tipo || "turno");
      setAllDay(!!turnoEditar?.allDay);

      if (turnoEditar?.plantilla_id) {
        setPlantillaId(String(turnoEditar.plantilla_id));
        setTipo("turno");
      } else {
        setPlantillaId("");
      }

      if (turnoEditar?.start && turnoEditar?.end) {
        setHoraInicio(hhmm(turnoEditar.start));
        setHoraFin(turnoEditar.allDay ? "23:59" : hhmm(turnoEditar.end));
      } else if (slotSeleccionado?.start && slotSeleccionado?.end) {
        setHoraInicio(hhmm(slotSeleccionado.start));
        setHoraFin(hhmm(slotSeleccionado.end));
      } else {
        setHoraInicio("");
        setHoraFin("");
      }
    }, [modalAbierto, turnoEditar, slotSeleccionado]);

    const submit = () => {
      setError("");
      if (!fechaSeleccionada) {
        setError("No se pudo determinar la fecha.");
        return;
      }

      // Plantilla
      if (plantillaId && plantillaId !== "personalizado") {
        const plantilla = (plantillas || []).find((p) => String(p.id) === String(plantillaId));
        if (!plantilla) {
          setError("Plantilla no encontrada.");
          return;
        }

        const bloques = (plantilla.segmentos || []).map((seg) => {
          const start = mkLocalDateTime(fechaSeleccionada, seg.hora_inicio);
          let end = mkLocalDateTime(fechaSeleccionada, seg.hora_fin);
          if ((seg.hora_fin || "") < (seg.hora_inicio || "")) end.setDate(end.getDate() + 1);
          return { start, end };
        });

        const haySolape = (todosLosTurnos || []).some((ev) =>
          bloques.some((b) => ev.start < b.end && ev.end > b.start)
        );

        if (haySolape) {
          setError("Algún segmento de la plantilla se solapa con otro existente.");
          return;
        }

        handleSaveTurno({
          plantilla_id: plantilla.id,
          fecha: fechaSeleccionada,
          descripcion,
        });
        return;
      }

      // Personalizado
      let hInicio = horaInicio;
      let hFin = horaFin;

      if (allDay) {
        hInicio = "00:00";
        hFin = "23:59";
      }

      if (!hInicio || !hFin) {
        setError("Debes indicar hora de inicio y fin.");
        return;
      }

      const inicio = mkLocalDateTime(fechaSeleccionada, hInicio);
      let fin = mkLocalDateTime(fechaSeleccionada, hFin);
      if (fin < inicio) fin.setDate(fin.getDate() + 1);

      const esEdicion = Boolean(turnoEditar?.id);
      const haySolape = (todosLosTurnos || []).some((ev) => {
        if (esEdicion && ev.id === turnoEditar.id) return false;
        return ev.start < fin && ev.end > inicio;
      });

      if (haySolape) {
        setError("El turno se solapa con otro existente.");
        return;
      }

      handleSaveTurno({
        tipo,
        descripcion,
        allDay,
        hora_inicio: hInicio,
        hora_fin: hFin,
        fecha: fechaSeleccionada,
        plantilla_id: null,
      });
    };

    return (
      <Dialog open={modalAbierto} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{turnoEditar ? "Editar turno" : "Nuevo turno"}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {fechaSeleccionada ? `Fecha: ${fechaSeleccionada}` : ""}
            </Typography>

            <Autocomplete
              size="small"
              options={plantillaOptions}
              getOptionLabel={(o) => o?.nombre || ""}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={plantillaValue}
              onChange={(_, v) => setPlantillaId(v ? String(v.id) : "")}
              renderInput={(params) => (
                <TextField {...params} label="Plantilla" placeholder="Selecciona una plantilla" />
              )}
              clearOnEscape
            />

            {plantillaId && plantillaId !== "personalizado" && plantillaResumen.length > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <b>Segmentos:</b>{" "}
                {plantillaResumen.map((s, i) => (
                  <span key={i}>
                    {s.hora_inicio} - {s.hora_fin}
                    {i < plantillaResumen.length - 1 ? ", " : ""}
                  </span>
                ))}
              </Typography>
            )}

            <TextField
              label="Notas / Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Opcional"
            />

            {/* ✅ FIX: MenuItem (MUI) */}
            <TextField
              select
              label="Tipo"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPlantillaId("personalizado");
              }}
              // plantilla (no personalizado) fuerza turno
              disabled={plantillaId && plantillaId !== "personalizado"}
            >
              <MenuItem value="turno">Turno</MenuItem>
              <MenuItem value="ausencia">Ausencia</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={allDay}
                  onChange={(e) => {
                    setAllDay(e.target.checked);
                    setPlantillaId("personalizado");
                  }}
                />
              }
              label="Todo el día"
            />

            {!allDay && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Hora inicio"
                  type="time"
                  value={horaInicio}
                  onChange={(e) => {
                    setHoraInicio(e.target.value);
                    setPlantillaId("personalizado");
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hora fin"
                  type="time"
                  value={horaFin}
                  onChange={(e) => {
                    setHoraFin(e.target.value);
                    setPlantillaId("personalizado");
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}

            {error && (
              <Typography color="error" sx={{ fontWeight: 600 }}>
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cancelar
          </Button>

          {turnoEditar ? (
            <Button variant="outlined" startIcon={<DeleteIcon />} onClick={handleDeleteTurno}>
              Eliminar
            </Button>
          ) : null}

          <Button onClick={submit} startIcon={<SaveIcon />}>
            {turnoEditar ? "Guardar cambios" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // =========================
  // Render
  // =========================
  const minTime = new Date(2000, 0, 1, 0, 0, 0, 0);
  const maxTime = new Date(2000, 0, 1, 23, 59, 59, 999);

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Gestión de turnos
        </Typography>

        {/* Arriba SOLO selector usuario, sin ocupar todo el ancho */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ maxWidth: 520 }}>
            <Autocomplete
              size="small"
              options={usuarioOptions}
              getOptionLabel={(u) => `${u?.nombre || ""} ${u?.apellidos || ""}`.trim()}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={usuarioObj}
              onChange={(_, v) => setUsuarioId(v ? String(v.id) : "")}
              loading={loadingUsuarios}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuario"
                  placeholder="Selecciona un usuario"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsuarios ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              clearOnEscape
            />
          </Box>
        </Paper>

        {/* Calendario */}
        <Paper
            sx={{
              p: 1,
              height: "78vh",
              overflow: "hidden",

              /* ===== Compactar toolbar RBC ===== */
              "& .rbc-toolbar": {
                padding: "0px 6px",
                marginBottom: "6px",
                minHeight: 36,
              },
              "& .rbc-toolbar-label": {
                padding: 0,
                lineHeight: "36px",
                fontSize: 14,
              },
              "& .rbc-btn-group button": {
                padding: "4px 10px",
                lineHeight: 1.2,
                minHeight: 32,
              },

              /* Opcional: el grupo de botones no “estire” por altura */
              "& .rbc-btn-group": {
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
              },
            }}
          >
          <Box sx={{ height: "100%" }}>
            <DragAndDropCalendar
              ref={calendarRef}
              localizer={localizer}
              events={todosLosTurnos}          // ✅ SIN filtrado manual
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              defaultView="week"
              views={["day", "week", "month", "agenda"]}
              onView={setVistaActual}
              onRangeChange={handleRangeChange}
              scrollToTime={new Date(2000, 0, 1, 7, 30)}
              min={minTime}
              max={maxTime}
              popup
              selectable
              resizable
              step={15}
              timeslots={4}
              components={{ event: CustomEvent }}
              messages={{
                date: "Fecha",
                time: "Hora",
                event: "Evento",
                allDay: "Todo el día",
                week: "Semana",
                work_week: "Semana laboral",
                day: "Día",
                month: "Mes",
                previous: "Ant",
                next: "Sig",
                yesterday: "Ayer",
                tomorrow: "Mañana",
                today: "Hoy",
                agenda: "Agenda",
                showMore: (total) => `+ Ver ${total} más`,
              }}
              style={{ height: "100%" }}
              eventPropGetter={eventPropGetter}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              draggableAccessor={(event) => event.activo}
            />
          </Box>
        </Paper>

        {loadingTurnos ? (
          <Typography sx={{ color: "text.secondary" }}>Cargando turnos…</Typography>
        ) : null}
      </Stack>

      {modalAbierto ? <ModalTurno /> : null}
    </Box>
  );
};
